import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  fetchAttribution,
  selectAttribution,
  selectAttributionStatus,
  selectAttributionError,
} from "../../../redux/slices/attribution-slice";

const PERIODS = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "1y", label: "1 year" },
];

const CHANNEL_COLOR = {
  JOB_BOARD: "#7B5AC4",
  SOCIAL: "#0A66C2",
  SEARCH: "#10B981",
  EMAIL: "#F59E0B",
  REFERRAL: "#EC4899",
  DIRECT: "#94A3B8",
};

export default function SourceAttributionTab() {
  const dispatch = useDispatch();
  const data = useSelector(selectAttribution);
  const status = useSelector(selectAttributionStatus);
  const error = useSelector(selectAttributionError);

  const [period, setPeriod] = useState("30d");
  const [sortKey, setSortKey] = useState("count");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    dispatch(fetchAttribution({ period }));
  }, [dispatch, period]);

  const flatRows = useMemo(() => {
    const rows = [];
    (data?.channels || []).forEach((ch) => {
      (ch.breakdown || []).forEach((b) => {
        const cnt = Number(b.count) || 0;
        const conv = Number(b.converted) || 0;
        rows.push({
          source: b.source,
          channel: ch.channel,
          count: cnt,
          converted: conv,
          conversion: cnt === 0 ? 0 : Math.round((conv * 100.0 / cnt) * 10) / 10,
        });
      });
    });
    return rows;
  }, [data]);

  const sortedRows = useMemo(() => {
    const out = [...flatRows];
    out.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "string") {
        const r = va.localeCompare(vb);
        return sortDir === "asc" ? r : -r;
      }
      const r = (va || 0) - (vb || 0);
      return sortDir === "asc" ? r : -r;
    });
    return out;
  }, [flatRows, sortKey, sortDir]);

  const barData = useMemo(() => {
    return flatRows.map((r) => ({ name: r.source, count: r.count, channel: r.channel }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [flatRows]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortIndicator = (key) =>
    sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold">
            Source attribution
          </div>
          <div className="text-sm text-vf-base mt-0.5">
            Where candidates come from over the selected window.
          </div>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded p-0.5">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                period === p.value
                  ? "bg-white text-vf-primary shadow-sm"
                  : "text-vf-muted hover:text-vf-base"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {status === "loading" && (
        <div className="py-12 text-center text-vf-muted text-sm">Loading attribution…</div>
      )}
      {status === "failed" && (
        <div className="py-12 text-center text-red-600 text-sm">{error || "Failed to load"}</div>
      )}
      {status === "succeeded" && data.totalCandidates === 0 && (
        <div className="py-12 text-center text-vf-muted text-sm">
          No applications captured in this window. Once candidates apply via the
          career page (with or without UTM params), they'll show up here.
        </div>
      )}
      {status === "succeeded" && data.totalCandidates > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-5">
            {(data.channels || []).map((ch) => (
              <div
                key={ch.channel}
                className="bg-white border border-slate-200 rounded-lg p-3"
                style={{ borderTop: `3px solid ${CHANNEL_COLOR[ch.channel] || "#94A3B8"}` }}
              >
                <div className="text-[10px] uppercase tracking-wide text-vf-muted font-semibold">
                  {ch.channel}
                </div>
                <div className="text-xl font-bold text-vf-base mt-1">{ch.count}</div>
                <div className="text-[11px] text-vf-muted mt-0.5">
                  {ch.conversionToHire?.toFixed?.(1) ?? 0}% conv
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-5">
            <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold mb-3">
              Top sources ({Math.min(10, barData.length)})
            </div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12 }}
                    formatter={(value, _name, p) => [
                      value,
                      `count (${p?.payload?.channel || "—"})`,
                    ]}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                    {barData.map((row, i) => (
                      <Bar key={i} fill={CHANNEL_COLOR[row.channel] || "#7B5AC4"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th onClick={() => toggleSort("source")}>Source{sortIndicator("source")}</Th>
                  <Th onClick={() => toggleSort("channel")}>Channel{sortIndicator("channel")}</Th>
                  <Th onClick={() => toggleSort("count")} align="right">Candidates{sortIndicator("count")}</Th>
                  <Th onClick={() => toggleSort("converted")} align="right">Converted{sortIndicator("converted")}</Th>
                  <Th onClick={() => toggleSort("conversion")} align="right">Conversion %{sortIndicator("conversion")}</Th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2 text-vf-base font-medium">{r.source}</td>
                    <td className="px-3 py-2">
                      <span
                        className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase"
                        style={{
                          background: (CHANNEL_COLOR[r.channel] || "#94A3B8") + "22",
                          color: CHANNEL_COLOR[r.channel] || "#475569",
                        }}
                      >
                        {r.channel}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-vf-muted">{r.converted}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.conversion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const Th = ({ children, onClick, align = "left" }) => (
  <th
    onClick={onClick}
    className={`px-3 py-2 text-${align} text-[11px] uppercase tracking-wide text-vf-muted font-semibold cursor-pointer hover:text-vf-base select-none`}
  >
    {children}
  </th>
);
